import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { T, SERIF, SECTIONS, RESULT_NARRATIVES, ROADMAPS, tierFor, NEXT_MOVES, TIERS } from "../lib/model";
import { ScoreGauge, DimensionRadar, TierScale, MaturityMeter, ConfidenceIndicator } from "./Charts";

const { FiArrowLeft, FiDownload, FiLoader, FiShield, FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiInfo, FiTarget, FiUser, FiCalendar, FiClock, FiFileText } = FiIcons;

export default function Report({ model, tier, answers, lead, scoreStatus, onBack }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const reportRef = useRef(null);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    setIsGenerating(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const element = reportRef.current;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#FFFFFF",
        windowWidth: 1024,
        onclone: (clonedDoc) => {
          const noPrintElements = clonedDoc.querySelectorAll('.no-print');
          noPrintElements.forEach(el => el.style.display = 'none');
        }
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = 210;
      const pdfHeight = 297;
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const fileName = `Human-Risk-Report-${lead?.company?.replace(/\s+/g, '-') || 'Executive'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("PDF Generation failed:", error);
      alert("Failed to generate PDF.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={{ background: "#F3F4F6", minHeight: "100vh" }}>
      {/* Sticky Header with Actions */}
      <header className="sticky top-0 z-50 bg-[#0D2440] px-6 py-4 md:px-10 shadow-lg no-print">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <button 
            onClick={onBack} 
            className="text-white flex items-center gap-2 text-sm font-bold hover:text-teal-400 transition-colors group"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => window.print()} 
              className="bg-white/10 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-white/20 transition-all border border-white/10"
            >
              Print
            </button>
            <button
              onClick={downloadPDF}
              disabled={isGenerating}
              className="bg-[#A4D82B] hover:bg-[#8ebc25] px-6 py-2.5 rounded-xl text-sm font-black text-[#0D2440] shadow-xl shadow-lime-900/20 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              ) : (
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
              )}
              {isGenerating ? "Processing..." : "Download PDF"}
            </button>
          </div>
        </div>
      </header>

      <div className="py-12 px-4 md:px-10">
        <main 
          ref={reportRef} 
          style={{ background: "#FFFFFF", fontFamily: "'Segoe UI', system-ui, sans-serif", color: T.ink }} 
          className="max-w-4xl mx-auto p-12 md:p-16 rounded-[40px] shadow-2xl overflow-hidden"
        >
          {/* Report Content */}
          <div className="border-b-2 border-gray-100 pb-12">
            <div className="flex justify-between items-start">
              <div>
                <div style={{ color: T.teal, letterSpacing: "0.2em", fontSize: 11, fontWeight: 900 }}>HUMAN RISK INDEX&trade;</div>
                <h1 style={{ fontFamily: SERIF, fontSize: 48, color: T.ink, lineHeight: 1.1 }} className="mt-4 font-black">Executive Risk Report</h1>
              </div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/ecb16a7902f793d76e4d541ba9ce1c3ae6d96976df461599e59c7024cc2713617bff44ec4f8cbf96d32b4bb87bd2d993/images/aW1hZ2UvcG5n/07cb85c4268f1317cf05349009b1d943.png" 
                alt="Logo" 
                style={{ height: 40, width: "auto" }} 
                className="opacity-90" 
              />
            </div>
            
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Company</div>
                <div className="text-sm font-black text-gray-900">{lead?.company || "Not Specified"}</div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Prepared For</div>
                <div className="text-sm font-black text-gray-900">{lead?.name || "Executive Team"}</div>
                <div className="text-[10px] font-bold text-gray-500">{lead?.role}</div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Risk Band</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: tier.color }}></div>
                  <span className="text-sm font-black uppercase tracking-tight" style={{ color: tier.color }}>{tier.name}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Date</div>
                <div className="text-sm font-black text-gray-900">{today}</div>
              </div>
            </div>
          </div>

          {/* Assessment Identity */}
          <section className="mt-8 p-6 bg-gray-50 rounded-3xl border border-gray-100 grid md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiFileText} className="text-blue-500 w-5 h-5" />
              <div>
                <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Assessment ID</div>
                <div className="text-xs font-black text-gray-700">{model.identity.id}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiInfo} className="text-blue-500 w-5 h-5" />
              <div>
                <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Methodology</div>
                <div className="text-xs font-black text-gray-700">v{model.identity.version}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiShield} className="text-blue-500 w-5 h-5" />
              <div>
                <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Status</div>
                <div className="text-xs font-black text-gray-700">{model.identity.status} Risk Band</div>
              </div>
            </div>
          </section>

          {/* Primary Result Section */}
          <section className="mt-12 bg-[#0D2440] rounded-[40px] p-10 text-white print-break-avoid">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="flex flex-col items-center">
                <div className="text-[11px] font-black uppercase tracking-widest text-teal-400 mb-6">Human Risk Exposure Profile</div>
                <ScoreGauge score={model.score} qualitative={true} />
                <div className="w-full mt-4"><TierScale score={model.score} /></div>
              </div>
              <div className="space-y-8">
                <div className="bg-white/5 rounded-3xl p-6 border border-white/10">
                  <MaturityMeter level={model.maturity} />
                </div>
                <div className="bg-white/5 rounded-3xl p-6 border border-white/10">
                  <ConfidenceIndicator pct={model.confidence} notice={model.confidenceNotice} />
                </div>
              </div>
            </div>
          </section>

          {/* Dimension Breakdown */}
          <section className="mt-16 print-break-avoid">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-blue-600" />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28 }} className="font-black text-gray-900">Domain Exposure Profile</h2>
            </div>
            <div className="bg-gray-50 rounded-[40px] p-10 border border-gray-100">
               <DimensionRadar dims={model.dims} />
            </div>
          </section>

          {/* Executive Summary */}
          <section className="mt-16 print-break-avoid">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600" />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28 }} className="font-black text-gray-900">Executive Interpretation</h2>
            </div>
            <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
              <p className="text-2xl font-black leading-tight text-gray-900">{RESULT_NARRATIVES[tier.name]}</p>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-2">Executive Meaning</div>
                <p className="text-gray-700 font-bold italic text-lg leading-relaxed">"{tier.meaning}"</p>
              </div>
            </div>
          </section>

          {/* Top Attack Paths */}
          {model.attackPaths.length > 0 && (
            <section className="mt-16 print-break-avoid">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600" />
                </div>
                <h2 style={{ fontFamily: SERIF, fontSize: 28 }} className="font-black text-gray-900">Top Attack Paths</h2>
              </div>
              <div className="space-y-6">
                {model.attackPaths.map((ap, i) => (
                  <div key={i} className="bg-gray-50 rounded-3xl p-8 border border-gray-100">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="px-3 py-1 bg-red-100 text-red-700 text-[10px] font-black uppercase rounded tracking-widest">Critical Path {i+1}</div>
                      <div className="text-sm font-bold text-gray-500 italic">"{ap.reason}"</div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 relative">
                      {/* Visual Connectors */}
                      <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-gray-200 -z-10 hidden md:block"></div>
                      
                      <div className="space-y-2">
                        <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Trigger</div>
                        <div className="p-3 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-700 min-h-[60px] flex items-center">{ap.trigger}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Failure</div>
                        <div className="p-3 bg-white border border-red-100 rounded-xl text-xs font-black text-red-700 min-h-[60px] flex items-center shadow-sm">{ap.failure}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Event</div>
                        <div className="p-3 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-700 min-h-[60px] flex items-center">{ap.event}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Impact</div>
                        <div className="p-3 bg-white border border-gray-100 rounded-xl text-xs font-black text-gray-700 min-h-[60px] flex items-center">{ap.impact}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Executive Decisions */}
          <section className="mt-16 print-break-avoid">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-teal-600" />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28 }} className="font-black text-gray-900">Executive Decisions</h2>
            </div>
            <div className="bg-[#0D2440] rounded-3xl overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="px-6 py-4 font-black text-teal-400 uppercase tracking-widest text-[10px]">Decision Required</th>
                    <th className="px-6 py-4 font-black text-teal-400 uppercase tracking-widest text-[10px]">Executive Owner</th>
                    <th className="px-6 py-4 font-black text-teal-400 uppercase tracking-widest text-[10px]">Target Date</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {model.executiveDecisions.map((d, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="px-6 py-4 font-bold">{d.decision}</td>
                      <td className="px-6 py-4 text-blue-200">{d.owner}</td>
                      <td className="px-6 py-4 text-blue-200">{d.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Action Ownership */}
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-orange-600" />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28 }} className="font-black text-gray-900">Remediation Action Items</h2>
            </div>
            
            <div className="space-y-6">
              {model.findings.map((f, i) => (
                <div key={i} className="p-8 bg-gray-50 rounded-3xl border border-gray-100 print-break-avoid">
                  <div className="flex gap-6">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg text-white shadow-xl flex-shrink-0" style={{ background: f.sev === 3 ? T.danger : f.sev === 2 ? T.orange : T.gold }}>0{i + 1}</div>
                    <div className="flex-1">
                      <p style={{ fontFamily: SERIF }} className="text-xl font-black text-gray-900 leading-snug">"{f.view}"</p>
                      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-gray-200">
                        <div>
                          <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest mb-1">Owner</div>
                          <div className="text-xs font-black text-gray-700 flex items-center gap-1">
                            <SafeIcon icon={FiUser} className="w-3 h-3 text-blue-500" />
                            {f.owner}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest mb-1">Deadline</div>
                          <div className="text-xs font-black text-gray-700 flex items-center gap-1">
                            <SafeIcon icon={FiCalendar} className="w-3 h-3 text-blue-500" />
                            {f.deadline}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest mb-1">Success Measure</div>
                          <div className="text-xs font-black text-gray-700 flex items-center gap-1">
                            <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-blue-500" />
                            {f.success}
                          </div>
                        </div>
                        <div>
                          <div className="text-[9px] font-black uppercase text-gray-400 tracking-widest mb-1">Status</div>
                          <div className="text-[10px] font-black text-orange-600 bg-orange-100 px-2 py-0.5 rounded inline-block">{f.status}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gray-900 rounded-3xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-8 print-break-avoid">
              <div className="flex-1">
                <div className="text-[10px] font-black uppercase text-lime-400 tracking-widest mb-2">Strategic Next Move</div>
                <p className="text-xl font-black leading-tight">{NEXT_MOVES[tier.name]}</p>
              </div>
              <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-lime-400 opacity-20 hidden md:block" />
            </div>
          </section>

          {/* Strategic Roadmap */}
          <section className="mt-20 bg-[#0D2440] rounded-[40px] p-12 text-white print:bg-white print:text-ink print:border print:border-gray-200 print-break-avoid">
            <div className="flex items-center gap-3 mb-8">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-lime-400" />
              <h2 style={{ fontFamily: SERIF, fontSize: 32 }} className="font-black text-white print:text-ink">Strategic Remediation Roadmap</h2>
            </div>
            <div className="grid gap-12">
              {ROADMAPS[tier.name].map((p, i) => (
                <div key={i} className="relative pl-10 border-l border-white/10 last:border-0 pb-2">
                  <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-lime-400 shadow-[0_0_10px_rgba(164,216,43,0.5)]" />
                  <div className="text-[10px] font-black uppercase text-lime-400 tracking-widest mb-1">Phase {i + 1}</div>
                  <h3 className="text-xl font-black tracking-tight">{p.phase}</h3>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    {p.goals.map((g, gi) => (
                      <div key={gi} className="bg-white/5 p-4 rounded-2xl border border-white/5 flex gap-3">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-lime-400 mt-1 flex-shrink-0" />
                        <span className="text-sm font-bold text-blue-50/80 leading-relaxed">{g}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Methodology Block */}
          <section className="mt-20 p-10 bg-gray-50 rounded-[40px] border border-gray-100 print-break-avoid">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiInfo} className="w-6 h-6 text-blue-600" />
              <h3 style={{ fontFamily: SERIF }} className="text-xl font-black text-gray-900">Assessment Methodology</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed font-medium">
              The Human Risk Index is a directional workforce-risk assessment designed to help business leaders identify common behavior-driven cybersecurity exposures. 
              It is not a penetration test, compliance audit, insurance review, or guarantee of security posture. 
              For a complete evaluation, pair this assessment with MFA adoption data, phishing simulation results, email security telemetry, and incident reporting history.
            </p>
          </section>

          {/* Footer Information */}
          <footer className="mt-20 pt-10 border-t border-gray-100 flex justify-between items-end">
            <div>
              <p className="text-sm font-black text-gray-900">Intelligent Provider Services</p>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Human-Risk Advisory &middot; Power Plays</p>
            </div>
            <div className="text-right">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/ecb16a7902f793d76e4d541ba9ce1c3ae6d96976df461599e59c7024cc2713617bff44ec4f8cbf96d32b4bb87bd2d993/images/aW1hZ2UvcG5n/07cb85c4268f1317cf05349009b1d943.png" 
                alt="Logo" 
                style={{ height: 20, width: "auto" }} 
                className="ml-auto grayscale opacity-30 mb-4" 
              />
              <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Human Risk Index&trade; &middot; Confidential Report</p>
            </div>
          </footer>
        </main>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; }
          main { 
            box-shadow: none !important; 
            padding: 0 !important; 
            margin: 0 !important; 
            max-width: 100% !important; 
            border-radius: 0 !important; 
          }
          .print-break-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}